import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Text } from '@lifeomic/chroma-react/components/Text';
import FhirService from '@lifeomic/phc-web-toolkit/src/services/FhirService';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import useServiceAuth from '@lifeomic/phc-web-toolkit/src/hooks/useServiceAuth';
import MarkdownViewer from '@lifeomic/phc-web-toolkit/src/components/MarkdownViewer';
import {
  useActiveAccountId,
  useActiveProjectId,
  useFeatures,
  useNotify,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import { useApiClient } from '@lifeomic/phc-web-toolkit/src/hooks/useRestAPIHooks';
import FileService from '@lifeomic/phc-web-toolkit/src/services/FileService';
import {
  DOCUMENT_EXTRACTION_V2_APPROVED_AT_EXTENSION_URL,
  DOCUMENT_EXTRACTION_V2_APPROVED_BY_EXTENSION_URL,
  DOCUMENT_EXTRACTION_V2_REVIEW_STATUS_EXTENSION_URL,
} from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/Measurements/Graphs/Ranges/ExtractionFeedbackActions';
import urls from '../../../../util/urls';
import { memberDetailMessages } from './messages';

export const DOCUMENT_EXTRACTION_V2_PROJECTION_IDENTIFIER_SYSTEM =
  'http://lifeomic.com/fhir/document-extraction-v2-projection';

const provenanceFhirService = new FhirService('Provenance');
const documentReferenceFhirService = new FhirService('DocumentReference');
const communicationFhirService = new FhirService('Communication');
const fileService = new FileService();

type ExtractedResource = fhir.Resource & {
  id?: string;
  resourceType?: string;
  extension?: fhir.Extension[];
  identifier?: Array<{ system?: string; value?: string }>;
};

type ResourceFeedbackDecision = 'approved' | 'rejected';

type SourceReference = {
  resourceType: string;
  id: string;
  reference: string;
};

type DocumentExtractionV2ProjectionResourceType =
  | 'AllergyIntolerance'
  | 'Condition'
  | 'Immunization'
  | 'MedicationStatement'
  | 'Observation'
  | 'Procedure';

type ExtractedResourceProvenanceProps = {
  resource: ExtractedResource;
  resourceType: string;
  subjectId?: string;
  onRejected?: (resourceId: string) => void;
};

const DOCUMENT_EXTRACTION_V2_PROJECTION_RESOURCE_TYPES = new Set<string>([
  'AllergyIntolerance',
  'Condition',
  'Immunization',
  'MedicationStatement',
  'Observation',
  'Procedure',
]);

const getSourceReference = (
  provenance: fhir.Provenance | null,
): SourceReference | null => {
  const source = provenance?.entity?.find(entity => entity.role === 'source');
  const reference = source?.whatReference?.reference;
  const match = reference?.match(/^([A-Za-z]+)\/(.+)$/);
  if (!reference || !match) {
    return null;
  }

  return {
    resourceType: match[1],
    id: match[2],
    reference,
  };
};

const getDocumentTitle = (
  document: fhir.DocumentReference | null,
  fallback: string,
): string =>
  document?.type?.text ||
  document?.description ||
  document?.content?.[0]?.attachment?.title ||
  document?.id ||
  fallback;

const getDocumentAttachmentFileId = (
  document: fhir.DocumentReference | null,
): string | null => {
  const attachmentUrl = document?.content?.find(
    content => !!content.attachment?.url,
  )?.attachment?.url;
  const fileId = attachmentUrl?.split('?')[0]?.split('/').filter(Boolean).pop();
  return fileId || null;
};

const getDocumentReviewUrl = (
  document: fhir.DocumentReference | null,
  accountId: string | undefined,
  projectId: string | undefined,
): string | null => {
  const fileId = getDocumentAttachmentFileId(document);
  if (!fileId || !accountId || !projectId) {
    return null;
  }

  return `/phc${urls.app.files({
    fileId,
    account: accountId,
    projectId,
  })}`;
};

const getSourceLabel = (
  sourceReference: SourceReference | null,
  sourceDocument: fhir.DocumentReference | null,
  sourceCommunication: fhir.Communication | null,
  fallback: string,
) => {
  if (!sourceReference) {
    return fallback;
  }

  if (sourceReference.resourceType === 'DocumentReference') {
    return getDocumentTitle(sourceDocument, fallback);
  }

  if (sourceReference.resourceType === 'Communication') {
    return `Communication/${sourceCommunication?.id ?? sourceReference.id}`;
  }

  return sourceReference.reference;
};

const getCommunicationTitle = (communication: fhir.Communication | null) =>
  communication?.category?.[0]?.text ||
  communication?.reasonCode?.[0]?.text ||
  communication?.id ||
  'Source communication';

const getCommunicationContent = (communication: fhir.Communication | null) =>
  (communication?.payload?.[0] as { contentString?: string } | undefined)
    ?.contentString ||
  communication?.note?.[0]?.text ||
  '';

export const isDocumentExtractionV2Resource = (resource: ExtractedResource) =>
  resource.identifier?.some(
    identifier =>
      identifier.system === DOCUMENT_EXTRACTION_V2_PROJECTION_IDENTIFIER_SYSTEM,
  ) ?? false;

const getExtensionValue = (
  resource: ExtractedResource,
  url: string,
  key: 'valueString' | 'valueDateTime',
) =>
  resource.extension?.find(extension => extension.url === url)?.[key] as
    | string
    | undefined;

const isSupportedFeedbackResourceType = (
  resourceType: string,
): resourceType is DocumentExtractionV2ProjectionResourceType =>
  DOCUMENT_EXTRACTION_V2_PROJECTION_RESOURCE_TYPES.has(resourceType);

export const ExtractedResourceProvenance: React.FC<
  ExtractedResourceProvenanceProps
> = ({ resource, resourceType, subjectId, onRejected }) => {
  const { formatMessage } = useIntl();
  const apiClient = useApiClient();
  const accountId = useActiveAccountId();
  const projectId = useActiveProjectId();
  const auth = useServiceAuth();
  const hasFeature = useFeatures();
  const [notify] = useNotify();
  const isEnabled = hasFeature('documentExtractionV2Ui');
  const [fetchProvenance] = useService(provenanceFhirService.search);
  const [fetchDocumentReference] = useService(
    documentReferenceFhirService.getById,
  );
  const [fetchCommunication] = useService(communicationFhirService.getById);
  const [fetchOpenLink] = useService(fileService.fetchOpenLink, {
    rethrowErrors: true,
  });
  const [provenance, setProvenance] = useState<fhir.Provenance | null>(null);
  const [sourceReference, setSourceReference] =
    useState<SourceReference | null>(null);
  const [sourceDocument, setSourceDocument] =
    useState<fhir.DocumentReference | null>(null);
  const [sourceCommunication, setSourceCommunication] =
    useState<fhir.Communication | null>(null);
  const [provenanceErrorMessage, setProvenanceErrorMessage] = useState<
    string | null
  >(null);
  const [decisionInFlight, setDecisionInFlight] =
    useState<ResourceFeedbackDecision | null>(null);
  const [isRejectConfirmOpen, setIsRejectConfirmOpen] = useState(false);
  const [isSourceCommunicationOpen, setIsSourceCommunicationOpen] =
    useState(false);
  const [approvedBy, setApprovedBy] = useState<string | undefined>(() =>
    getExtensionValue(
      resource,
      DOCUMENT_EXTRACTION_V2_APPROVED_BY_EXTENSION_URL,
      'valueString',
    ),
  );
  const [approvedAt, setApprovedAt] = useState<string | undefined>(() =>
    getExtensionValue(
      resource,
      DOCUMENT_EXTRACTION_V2_APPROVED_AT_EXTENSION_URL,
      'valueDateTime',
    ),
  );
  const [reviewStatus, setReviewStatus] = useState<string | undefined>(() =>
    getExtensionValue(
      resource,
      DOCUMENT_EXTRACTION_V2_REVIEW_STATUS_EXTENSION_URL,
      'valueString',
    ),
  );

  const isExtracted = useMemo(
    () => isDocumentExtractionV2Resource(resource),
    [resource],
  );

  const loadProvenance = useCallback(async () => {
    if (
      !isEnabled ||
      !isExtracted ||
      !accountId ||
      !projectId ||
      !auth ||
      !resource.id
    ) {
      setProvenance(null);
      setSourceReference(null);
      setSourceDocument(null);
      setSourceCommunication(null);
      setProvenanceErrorMessage(null);
      return;
    }

    try {
      const provenanceResponse = await fetchProvenance(accountId, projectId, {
        target: `${resourceType}/${resource.id}`,
        _count: 1,
      });
      const nextProvenance =
        provenanceResponse?.data?.entry?.[0]?.resource ?? null;
      setProvenance(nextProvenance);
      setProvenanceErrorMessage(null);

      const nextSourceReference = getSourceReference(nextProvenance);
      setSourceReference(current =>
        current?.reference === nextSourceReference?.reference
          ? current
          : nextSourceReference,
      );
      if (!nextSourceReference) {
        setSourceDocument(null);
        setSourceCommunication(null);
        return;
      }

      if (nextSourceReference.resourceType === 'DocumentReference') {
        const documentResponse = await fetchDocumentReference(
          accountId,
          nextSourceReference.id,
        );
        setSourceDocument(documentResponse?.data ?? null);
        setSourceCommunication(null);
      } else if (nextSourceReference.resourceType === 'Communication') {
        const communicationResponse = await fetchCommunication(
          accountId,
          nextSourceReference.id,
        );
        setSourceCommunication(communicationResponse?.data ?? null);
        setSourceDocument(null);
      } else {
        setSourceDocument(null);
        setSourceCommunication(null);
      }
    } catch {
      setProvenance(null);
      setSourceReference(null);
      setSourceDocument(null);
      setSourceCommunication(null);
      setProvenanceErrorMessage(
        formatMessage(
          memberDetailMessages.memberDocumentsExtractionProvenanceLoadError,
        ),
      );
    }
  }, [
    accountId,
    auth,
    fetchCommunication,
    fetchDocumentReference,
    fetchProvenance,
    formatMessage,
    isEnabled,
    isExtracted,
    projectId,
    resource.id,
    resourceType,
  ]);

  useEffect(() => {
    void loadProvenance();
  }, [loadProvenance]);

  useEffect(() => {
    setApprovedBy(
      getExtensionValue(
        resource,
        DOCUMENT_EXTRACTION_V2_APPROVED_BY_EXTENSION_URL,
        'valueString',
      ),
    );
    setApprovedAt(
      getExtensionValue(
        resource,
        DOCUMENT_EXTRACTION_V2_APPROVED_AT_EXTENSION_URL,
        'valueDateTime',
      ),
    );
    setReviewStatus(
      getExtensionValue(
        resource,
        DOCUMENT_EXTRACTION_V2_REVIEW_STATUS_EXTENSION_URL,
        'valueString',
      ),
    );
  }, [resource]);

  const submitFeedback = useCallback(
    async (decision: ResourceFeedbackDecision) => {
      if (
        !resource.id ||
        !subjectId ||
        !projectId ||
        !isSupportedFeedbackResourceType(resourceType)
      ) {
        return;
      }

      setDecisionInFlight(decision);
      try {
        const response = await apiClient.request(
          'POST /v1/agents-v2/document-extraction-v2/resources/:resource_type/:resource_id/feedback',
          {
            resource_type: resourceType,
            resource_id: resource.id,
            subject_id: subjectId,
            project_id: projectId,
            decision,
          },
        );

        if (decision === 'approved') {
          setApprovedBy(response.data.approved_by ?? undefined);
          setApprovedAt(response.data.approved_at ?? undefined);
          setReviewStatus('approved');
          notify(
            formatMessage(
              memberDetailMessages.memberDocumentsExtractionProvenanceApproveSuccess,
            ),
            'success',
          );
        } else {
          onRejected?.(response.data.resource_id);
          notify(
            formatMessage(
              memberDetailMessages.memberDocumentsExtractionProvenanceRejectSuccess,
            ),
            'success',
          );
        }
      } catch {
        notify(
          formatMessage(
            memberDetailMessages.memberDocumentsExtractionProvenanceFeedbackError,
          ),
          'error',
        );
      } finally {
        setDecisionInFlight(null);
        setIsRejectConfirmOpen(false);
      }
    },
    [
      apiClient,
      formatMessage,
      notify,
      onRejected,
      projectId,
      resource.id,
      resourceType,
      subjectId,
    ],
  );

  if (!isEnabled || !isExtracted) {
    return null;
  }

  const isFeedbackSupported =
    !!resource.id &&
    !!subjectId &&
    !!projectId &&
    isSupportedFeedbackResourceType(resourceType);
  const isApproved = reviewStatus === 'approved';
  const sourceDocumentReviewUrl =
    sourceReference?.resourceType === 'DocumentReference'
      ? getDocumentReviewUrl(sourceDocument, accountId, projectId)
      : null;
  const canOpenSourceCommunication =
    sourceReference?.resourceType === 'Communication' && !!sourceCommunication;
  const sourceDocumentFileId = getDocumentAttachmentFileId(sourceDocument);
  const sourceLabel = getSourceLabel(
    sourceReference,
    sourceDocument,
    sourceCommunication,
    formatMessage(
      memberDetailMessages.memberDocumentsExtractionProvenanceUnknownSourceDocument,
    ),
  );

  const openSourceDocument = async (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (!sourceDocumentFileId || !projectId) {
      return;
    }

    try {
      const response = await fetchOpenLink(
        sourceDocumentFileId,
        subjectId ? { project: projectId, subjectId } : undefined,
      );
      const openUrl = response?.data?.downloadUrl;
      if (openUrl) {
        window.open(openUrl, '_blank', 'noopener,noreferrer');
      }
    } catch {
      notify(
        formatMessage(
          memberDetailMessages.memberDocumentsExtractionProvenanceLoadError,
        ),
        'error',
      );
    }
  };

  return (
    <Box
      direction="column"
      gap={1}
      marginTop={1}
      padding={0}
      data-testid="document-extraction-v2-resource-provenance"
    >
      {!isApproved ? (
        <Text>
          {formatMessage(
            memberDetailMessages.memberDocumentsExtractionProvenanceDescription,
          )}
        </Text>
      ) : null}
      <Text>
        <FormattedMessage
          {...memberDetailMessages.memberDocumentsExtractionProvenanceSourceDocument}
          values={{
            source: sourceDocumentReviewUrl ? (
              <a
                href={sourceDocumentReviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={openSourceDocument}
              >
                {sourceLabel}
              </a>
            ) : canOpenSourceCommunication ? (
              <Button
                variant="text"
                onClick={event => {
                  event.stopPropagation();
                  setIsSourceCommunicationOpen(true);
                }}
              >
                {sourceLabel}
              </Button>
            ) : (
              sourceLabel
            ),
          }}
        />
      </Text>
      {provenance?.recorded && (
        <Text>
          {formatMessage(
            memberDetailMessages.memberDocumentsExtractionProvenanceExtractedAt,
            { recorded: provenance.recorded },
          )}
        </Text>
      )}
      {provenanceErrorMessage && <Text>{provenanceErrorMessage}</Text>}
      {isApproved && approvedBy ? (
        <Text>
          {formatMessage(
            memberDetailMessages.memberDocumentsExtractionProvenanceApproved,
            {
              user: approvedBy,
              time: approvedAt ?? '',
            },
          )}
        </Text>
      ) : null}
      {isFeedbackSupported ? (
        <Box direction="row" gap={1} marginTop={1}>
          <Button
            variant="outlined"
            disabled={!!decisionInFlight || isApproved}
            onClick={event => {
              event.stopPropagation();
              void submitFeedback('approved');
            }}
          >
            {decisionInFlight === 'approved'
              ? formatMessage(
                  memberDetailMessages.memberDocumentsExtractionProvenanceApproving,
                )
              : formatMessage(
                  memberDetailMessages.memberDocumentsExtractionProvenanceApprove,
                )}
          </Button>
          <Button
            variant="outlined"
            color="negative"
            disabled={!!decisionInFlight}
            onClick={event => {
              event.stopPropagation();
              setIsRejectConfirmOpen(true);
            }}
          >
            {formatMessage(
              memberDetailMessages.memberDocumentsExtractionProvenanceReject,
            )}
          </Button>
        </Box>
      ) : null}
      <Modal
        isOpen={isSourceCommunicationOpen}
        title={getCommunicationTitle(sourceCommunication)}
        onDismiss={() => setIsSourceCommunicationOpen(false)}
      >
        <MarkdownViewer
          markdown={getCommunicationContent(sourceCommunication)}
        />
      </Modal>
      <Modal
        isOpen={isRejectConfirmOpen}
        title={formatMessage(
          memberDetailMessages.memberDocumentsExtractionProvenanceRejectTitle,
        )}
        onDismiss={() => setIsRejectConfirmOpen(false)}
        actions={
          <>
            <Button
              variant="text"
              onClick={() => setIsRejectConfirmOpen(false)}
              disabled={!!decisionInFlight}
            >
              {formatMessage(
                memberDetailMessages.memberDocumentsExtractionProvenanceCancel,
              )}
            </Button>
            <Button
              color="negative"
              onClick={() => submitFeedback('rejected')}
              disabled={!!decisionInFlight}
            >
              {decisionInFlight === 'rejected'
                ? formatMessage(
                    memberDetailMessages.memberDocumentsExtractionProvenanceRejecting,
                  )
                : formatMessage(
                    memberDetailMessages.memberDocumentsExtractionProvenanceReject,
                  )}
            </Button>
          </>
        }
      >
        <Text>
          {formatMessage(
            memberDetailMessages.memberDocumentsExtractionProvenanceRejectDescription,
          )}
        </Text>
      </Modal>
    </Box>
  );
};
